/**
 * IconComponent — resolves an icon name string to a rendered icon element.
 *
 * Supported naming conventions:
 *
 * 1. MUI / react-icons names (e.g. "Settings", "Slack")
 *    Mapped statically in ICON_MAP below.
 *
 * 2. devicon font icons — prefix "devicon-" (e.g. "devicon-react-original")
 *    Rendered as <i className={iconName} />.
 *    The devicon CSS is imported globally in index.tsx from the "devicon" npm package.
 *    Append the "colored" class to get brand colors: "devicon-react-original colored".
 *    Full icon list: https://devicon.dev
 *
 * 3. devicon SVG (colored) — prefix "devicon-svg:" (e.g. "devicon-svg:react/react-original")
 *    Rendered as <img src="{PUBLIC_URL}/devicons/{path}.svg" />.
 *    SVG files are copied from node_modules/devicon/icons to public/devicons at prestart/prebuild
 *    via the "copy-devicons" npm script (uses copyfiles). The public/devicons folder is gitignored.
 */
import React from 'react';
import { type IconType } from 'react-icons';
import { TbBrandSlack, TbBrandTeams } from 'react-icons/tb';

import ConfigContext from '../../components/ConfigContext';

import VoiceChatIcon from '@mui/icons-material/VoiceChat';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import {
	Accessibility as AccessibilityIcon,
	AccountBalance as AccountBalanceIcon,
	AccountCircle as AccountCircleIcon,
	AccountTree as AccountTreeIcon,
	AddCircle as AddCircleIcon,
	AddCircle as AddIcon,
	Air as AirIcon,
	Alarm as AlarmIcon,
	AllInclusive as AllInclusiveIcon,
	AlternateEmail as AlternateEmailIcon,
	Analytics as AnalyticsIcon,
	Api as ApiIcon,
	Archive as ArchiveIcon,
	ArrowBack as ArrowBackIcon,
	ArrowBackIos as ArrowBackIosIcon,
	ArrowCircleLeft as ArrowCircleLeftIcon,
	ArrowCircleRight as ArrowCircleRightIcon,
	ArrowDropDownCircle as ArrowDropDownCircleIcon,
	ArrowDropDown as ArrowDropDownIcon,
	ArrowForward as ArrowForwardIcon,
	ArrowForwardIos as ArrowForwardIosIcon,
	ArrowLeft as ArrowLeftIcon,
	ArrowOutward as ArrowOutwardIcon,
	ArrowRight as ArrowRightIcon,
	Article as ArticleIcon,
	Assessment as AssessmentIcon,
	Assistant as AssistantIcon,
	AttachFile as AttachFileIcon,
	AutoAwesome as AutoAwesomeIcon,
	Badge as BadgeIcon,
	BarChart as BarChartIcon,
	Bolt as BoltIcon,
	Book as BookIcon,
	Bookmark as BookmarkIcon,
	Brush as BrushIcon,
	BugReport as BugReportIcon,
	Build as BuildIcon,
	Business as BusinessIcon,
	CalendarMonth as CalendarMonthIcon,
	CalendarToday as CalendarTodayIcon,
	CallMade as CallMadeIcon,
	Campaign as CampaignIcon,
	Cancel as CancelIcon,
	Category as CategoryIcon,
	Chat as ChatIcon,
	CheckCircle as CheckCircleIcon,
	Check as CheckIcon,
	Checklist as ChecklistIcon,
	Close as CloseIcon,
	CloudDownload as CloudDownloadIcon,
	Cloud as CloudIcon,
	CloudOff as CloudOffIcon,
	CloudUpload as CloudUploadIcon,
	Code as CodeIcon,
	ContactEmergency as ContactEmergencyIcon,
	ContactMail as ContactMailIcon,
	ContactPhone as ContactPhoneIcon,
	Contacts as ContactsIcon,
	CreditCard as CreditCardIcon,
	Dashboard as DashboardIcon,
	DataObject as DataObjectIcon,
	DateRange as DateRangeIcon,
	Delete as DeleteIcon,
	Description as DescriptionIcon,
	Devices as DevicesIcon,
	Domain as DomainIcon,
	Download as DownloadIcon,
	Edit as EditIcon,
	Email as EmailIcon,
	EmojiEvents as EmojiEventsIcon,
	Error as ErrorIcon,
	Event as EventIcon,
	Extension as ExtensionIcon,
	Favorite as FavoriteIcon,
	FileCopy as FileCopyIcon,
	FileOpen as FileOpenIcon,
	FindInPage as FindInPageIcon,
	Flag as FlagIcon,
	Folder as FolderIcon,
	FolderZip as FolderZipIcon,
	Functions as FunctionsIcon,
	Gavel as GavelIcon,
	GridView as GridViewIcon,
	GroupAdd as GroupAddIcon,
	Group as GroupIcon,
	Help as HelpIcon,
	HexagonOutlined as HexagonOutlinedIcon,
	History as HistoryIcon,
	Home as HomeIcon,
	House as HouseIcon,
	Hub as HubIcon,
	Image as ImageIcon,
	Inbox as InboxIcon,
	Info as InfoIcon,
	InsertDriveFile as InsertDriveFileIcon,
	Insights as InsightsIcon,
	IntegrationInstructions as IntegrationInstructionsIcon,
	Inventory as InventoryIcon,
	Key as KeyIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	KeyboardArrowUp as KeyboardArrowUpIcon,
	Label as LabelIcon,
	Language as LanguageIcon,
	Laptop as LaptopIcon,
	Layers as LayersIcon,
	Lightbulb as LightbulbIcon,
	Link as LinkIcon,
	LinkOff as LinkOffIcon,
	List as ListIcon,
	LocalAtm as LocalAtmIcon,
	LocationOn as LocationOnIcon,
	Lock as LockIcon,
	Mail as MailIcon,
	MailOutline as MailOutlineIcon,
	Map as MapIcon,
	Memory as MemoryIcon,
	Menu as MenuIcon,
	Message as MessageIcon,
	MonetizationOn as MonetizationOnIcon,
	Monitor as MonitorIcon,
	Note as NoteIcon,
	Notifications as NotificationsIcon,
	OpenInNew as OpenInNewIcon,
	Palette as PaletteIcon,
	Pause as PauseIcon,
	Payment as PaymentIcon,
	People as PeopleIcon,
	Percent as PercentIcon,
	Person as PersonIcon,
	Phone as PhoneIcon,
	PieChart as PieChartIcon,
	PlayArrow as PlayArrowIcon,
	Psychology as PsychologyIcon,
	Public as PublicIcon,
	Receipt as ReceiptIcon,
	Rectangle as RectangleIcon,
	Refresh as RefreshIcon,
	Report as ReportIcon,
	RocketLaunch as RocketLaunchIcon,
	Route as RouteIcon,
	Rule as RuleIcon,
	Save as SaveIcon,
	Schedule as ScheduleIcon,
	Schema as SchemaIcon,
	Science as ScienceIcon,
	Search as SearchIcon,
	Security as SecurityIcon,
	Send as SendIcon,
	Settings as SettingsIcon,
	Share as ShareIcon,
	ShoppingCart as ShoppingCartIcon,
	SmartToy as SmartToyIcon,
	Smartphone as SmartphoneIcon,
	Sms as SmsIcon,
	Speed as SpeedIcon,
	Star as StarIcon,
	Start as StartIcon,
	Stop as StopIcon,
	Storage as StorageIcon,
	Summarize as SummarizeIcon,
	SupervisorAccount as SupervisorAccountIcon,
	TableChart as TableChartIcon,
	Tag as TagIcon,
	Task as TaskIcon,
	Terminal as TerminalIcon,
	ThumbUp as ThumbUpIcon,
	Timeline as TimelineIcon,
	Timer as TimerIcon,
	TimerOff as TimerOffIcon,
	TrendingUp as TrendingUpIcon,
	Tune as TuneIcon,
	Unarchive as UnarchiveIcon,
	Undo as UndoIcon,
	UploadFile as UploadFileIcon,
	Upload as UploadIcon,
	Verified as VerifiedIcon,
	VideoCall as VideoCallIcon,
	ViewList as ViewListIcon,
	Visibility as VisibilityIcon,
	Warning as WarningIcon,
	Web as WebIcon,
	Widgets as WidgetsIcon,
	Work as WorkIcon,
	X as XIcon,
} from '@mui/icons-material';

// IMPORTANT: Icon names in this map must match those in app-runtime/src/common/components/IconComponent.tsx
// to ensure consistent icon availability across both designer and runtime.
const ICON_MAP: Record<string, React.ReactElement> = {
	Accessibility: <AccessibilityIcon fontSize="small" />,
	AccountBalance: <AccountBalanceIcon fontSize="small" />,
	AccountCircle: <AccountCircleIcon fontSize="small" />,
	AccountTree: <AccountTreeIcon fontSize="small" />,
	Add: <AddIcon fontSize="small" />,
	AddCircle: <AddCircleIcon fontSize="small" />,
	Air: <AirIcon fontSize="small" />,
	Alarm: <AlarmIcon fontSize="small" />,
	AllInclusive: <AllInclusiveIcon fontSize="small" />,
	AlternateEmail: <AlternateEmailIcon fontSize="small" />,
	Analytics: <AnalyticsIcon fontSize="small" />,
	Api: <ApiIcon fontSize="small" />,
	Archive: <ArchiveIcon fontSize="small" />,
	ArrowBack: <ArrowBackIcon fontSize="small" />,
	ArrowBackIos: <ArrowBackIosIcon fontSize="small" />,
	ArrowCircleLeft: <ArrowCircleLeftIcon fontSize="small" />,
	ArrowCircleRight: <ArrowCircleRightIcon fontSize="small" />,
	ArrowDropDown: <ArrowDropDownIcon fontSize="small" />,
	ArrowDropDownCircle: <ArrowDropDownCircleIcon fontSize="small" />,
	ArrowForward: <ArrowForwardIcon fontSize="small" />,
	ArrowForwardIos: <ArrowForwardIosIcon fontSize="small" />,
	ArrowLeft: <ArrowLeftIcon fontSize="small" />,
	ArrowOutward: <ArrowOutwardIcon fontSize="small" />,
	ArrowRight: <ArrowRightIcon fontSize="small" />,
	Article: <ArticleIcon fontSize="small" />,
	Assessment: <AssessmentIcon fontSize="small" />,
	Assistant: <AssistantIcon fontSize="small" />,
	AttachFile: <AttachFileIcon fontSize="small" />,
	AutoAwesome: <AutoAwesomeIcon fontSize="small" />,
	Badge: <BadgeIcon fontSize="small" />,
	BarChart: <BarChartIcon fontSize="small" />,
	Bolt: <BoltIcon fontSize="small" />,
	Book: <BookIcon fontSize="small" />,
	Bookmark: <BookmarkIcon fontSize="small" />,
	Brush: <BrushIcon fontSize="small" />,
	BugReport: <BugReportIcon fontSize="small" />,
	Build: <BuildIcon fontSize="small" />,
	Business: <BusinessIcon fontSize="small" />,
	CalendarMonth: <CalendarMonthIcon fontSize="small" />,
	CalendarToday: <CalendarTodayIcon fontSize="small" />,
	CallMade: <CallMadeIcon fontSize="small" />,
	Campaign: <CampaignIcon fontSize="small" />,
	Cancel: <CancelIcon fontSize="small" />,
	Category: <CategoryIcon fontSize="small" />,
	Chat: <ChatIcon fontSize="small" />,
	Check: <CheckIcon fontSize="small" />,
	CheckCircle: <CheckCircleIcon fontSize="small" />,
	Checklist: <ChecklistIcon fontSize="small" />,
	Close: <CloseIcon fontSize="small" />,
	Cloud: <CloudIcon fontSize="small" />,
	CloudDownload: <CloudDownloadIcon fontSize="small" />,
	CloudOff: <CloudOffIcon fontSize="small" />,
	CloudUpload: <CloudUploadIcon fontSize="small" />,
	Code: <CodeIcon fontSize="small" />,
	Contact: <ContactPhoneIcon fontSize="small" />,
	ContactEmail: <ContactMailIcon fontSize="small" />,
	ContactEmergency: <ContactEmergencyIcon fontSize="small" />,
	ContactMail: <ContactMailIcon fontSize="small" />,
	Contacts: <ContactsIcon fontSize="small" />,
	CreditCard: <CreditCardIcon fontSize="small" />,
	Dashboard: <DashboardIcon fontSize="small" />,
	DataObject: <DataObjectIcon fontSize="small" />,
	Database: <StorageIcon fontSize="small" />,
	DateRange: <DateRangeIcon fontSize="small" />,
	Delete: <DeleteIcon fontSize="small" />,
	Description: <DescriptionIcon fontSize="small" />,
	Devices: <DevicesIcon fontSize="small" />,
	Document: <DescriptionIcon fontSize="small" />,
	Domain: <DomainIcon fontSize="small" />,
	Download: <DownloadIcon fontSize="small" />,
	Edit: <EditIcon fontSize="small" />,
	Email: <EmailIcon fontSize="small" />,
	EmojiEvents: <EmojiEventsIcon fontSize="small" />,
	Error: <ErrorIcon fontSize="small" />,
	Event: <EventIcon fontSize="small" />,
	Extension: <ExtensionIcon fontSize="small" />,
	Favorite: <FavoriteIcon fontSize="small" />,
	FileCopy: <FileCopyIcon fontSize="small" />,
	FileOpen: <FileOpenIcon fontSize="small" />,
	FileText: <ArticleIcon fontSize="small" />,
	FindInPage: <FindInPageIcon fontSize="small" />,
	Flag: <FlagIcon fontSize="small" />,
	Folder: <FolderIcon fontSize="small" />,
	FolderZip: <FolderZipIcon fontSize="small" />,
	Functions: <FunctionsIcon fontSize="small" />,
	Gavel: <GavelIcon fontSize="small" />,
	GridView: <GridViewIcon fontSize="small" />,
	Group: <GroupIcon fontSize="small" />,
	GroupAdd: <GroupAddIcon fontSize="small" />,
	Help: <HelpIcon fontSize="small" />,
	HexagonOutlined: <HexagonOutlinedIcon fontSize="small" />,
	History: <HistoryIcon fontSize="small" />,
	Home: <HomeIcon fontSize="small" />,
	House: <HouseIcon fontSize="small" />,
	Hub: <HubIcon fontSize="small" />,
	Image: <ImageIcon fontSize="small" />,
	Inbox: <InboxIcon fontSize="small" />,
	Info: <InfoIcon fontSize="small" />,
	InsertDriveFile: <InsertDriveFileIcon fontSize="small" />,
	Insights: <InsightsIcon fontSize="small" />,
	IntegrationInstructions: <IntegrationInstructionsIcon fontSize="small" />,
	Inventory: <InventoryIcon fontSize="small" />,
	Key: <KeyIcon fontSize="small" />,
	KeyboardArrowDown: <KeyboardArrowDownIcon fontSize="small" />,
	KeyboardArrowUp: <KeyboardArrowUpIcon fontSize="small" />,
	Label: <LabelIcon fontSize="small" />,
	Language: <LanguageIcon fontSize="small" />,
	Laptop: <LaptopIcon fontSize="small" />,
	Layers: <LayersIcon fontSize="small" />,
	Lightbulb: <LightbulbIcon fontSize="small" />,
	Link: <LinkIcon fontSize="small" />,
	LinkOff: <LinkOffIcon fontSize="small" />,
	List: <ListIcon fontSize="small" />,
	LocalAtm: <LocalAtmIcon fontSize="small" />,
	LocationOn: <LocationOnIcon fontSize="small" />,
	Lock: <LockIcon fontSize="small" />,
	Mail: <MailIcon fontSize="small" />,
	MailOutline: <MailOutlineIcon fontSize="small" />,
	Map: <MapIcon fontSize="small" />,
	Memory: <MemoryIcon fontSize="small" />,
	Menu: <MenuIcon fontSize="small" />,
	Message: <MessageIcon fontSize="small" />,
	MicrosoftTeams: (TbBrandTeams as IconType)({ size: 20 }) as React.ReactElement,
	MonetizationOn: <MonetizationOnIcon fontSize="small" />,
	Monitor: <MonitorIcon fontSize="small" />,
	Note: <NoteIcon fontSize="small" />,
	Notifications: <NotificationsIcon fontSize="small" />,
	OpenInNew: <OpenInNewIcon fontSize="small" />,
	Palette: <PaletteIcon fontSize="small" />,
	Pause: <PauseIcon fontSize="small" />,
	Payment: <PaymentIcon fontSize="small" />,
	People: <PeopleIcon fontSize="small" />,
	Percent: <PercentIcon fontSize="small" />,
	Person: <PersonIcon fontSize="small" />,
	Phone: <PhoneIcon fontSize="small" />,
	PhoneInTalk: <PhoneInTalkIcon fontSize="small" />,
	PieChart: <PieChartIcon fontSize="small" />,
	PlayArrow: <PlayArrowIcon fontSize="small" />,
	Psychology: <PsychologyIcon fontSize="small" />,
	Public: <PublicIcon fontSize="small" />,
	Receipt: <ReceiptIcon fontSize="small" />,
	Refresh: <RefreshIcon fontSize="small" />,
	Report: <ReportIcon fontSize="small" />,
	Rectangle: <RectangleIcon fontSize="small" />,
	RocketLaunch: <RocketLaunchIcon fontSize="small" />,
	Route: <RouteIcon fontSize="small" />,
	Rule: <RuleIcon fontSize="small" />,
	Save: <SaveIcon fontSize="small" />,
	Schedule: <ScheduleIcon fontSize="small" />,
	Schema: <SchemaIcon fontSize="small" />,
	Science: <ScienceIcon fontSize="small" />,
	Search: <SearchIcon fontSize="small" />,
	Security: <SecurityIcon fontSize="small" />,
	Send: <SendIcon fontSize="small" />,
	Settings: <SettingsIcon fontSize="small" />,
	Share: <ShareIcon fontSize="small" />,
	ShoppingCart: <ShoppingCartIcon fontSize="small" />,
	Slack: (TbBrandSlack as IconType)({ size: 20 }) as React.ReactElement,
	SmartToy: <SmartToyIcon fontSize="small" />,
	Smartphone: <SmartphoneIcon fontSize="small" />,
	Sms: <SmsIcon fontSize="small" />,
	Speed: <SpeedIcon fontSize="small" />,
	Star: <StarIcon fontSize="small" />,
	Start: <StartIcon fontSize="small" />,
	Stop: <StopIcon fontSize="small" />,
	Storage: <StorageIcon fontSize="small" />,
	Summarize: <SummarizeIcon fontSize="small" />,
	SupervisorAccount: <SupervisorAccountIcon fontSize="small" />,
	TableChart: <TableChartIcon fontSize="small" />,
	Tag: <TagIcon fontSize="small" />,
	Task: <TaskIcon fontSize="small" />,
	Terminal: <TerminalIcon fontSize="small" />,
	ThumbUp: <ThumbUpIcon fontSize="small" />,
	Timeline: <TimelineIcon fontSize="small" />,
	Timer: <TimerIcon fontSize="small" />,
	TimerOff: <TimerOffIcon fontSize="small" />,
	TrendingUp: <TrendingUpIcon fontSize="small" />,
	Tune: <TuneIcon fontSize="small" />,
	Unarchive: <UnarchiveIcon fontSize="small" />,
	Undo: <UndoIcon fontSize="small" />,
	Upload: <UploadIcon fontSize="small" />,
	UploadFile: <UploadFileIcon fontSize="small" />,
	VideoCall: <VideoCallIcon fontSize="small" />,
	ViewList: <ViewListIcon fontSize="small" />,
	Visibility: <VisibilityIcon fontSize="small" />,
	VoiceChat: <VoiceChatIcon fontSize="small" />,
	Verified: <VerifiedIcon fontSize="small" />,
	Warning: <WarningIcon fontSize="small" />,
	Web: <WebIcon fontSize="small" />,
	Widgets: <WidgetsIcon fontSize="small" />,
	Work: <WorkIcon fontSize="small" />,
	X: <XIcon fontSize="small" />,
};

const AVAILABLE_ICON_NAMES = Object.keys(ICON_MAP).sort();

export function isKnownIcon(iconName: string): boolean {
	return iconName.startsWith('devicon-') || iconName.startsWith('devicon-svg:') || iconName in ICON_MAP;
}

export function getAvailableIconNames(): string[] {
	return AVAILABLE_ICON_NAMES;
}

type IconComponentProps = {
	iconUrl?: string;
	iconName?: string;
	/** Constrains image-based marks by height while preserving their intrinsic aspect ratio. */
	preserveAspectRatio?: boolean;
	/** Optional maximum width for an aspect-ratio-preserving image mark. */
	maxWidth?: string;
};

function parseIconUrl(iconUrl: string) {
	const hashIndex = iconUrl.indexOf('#');
	const urlWithoutHash = hashIndex >= 0 ? iconUrl.slice(0, hashIndex) : iconUrl;
	const hash = hashIndex >= 0 ? iconUrl.slice(hashIndex) : '';
	const queryIndex = urlWithoutHash.indexOf('?');

	if (queryIndex < 0) {
		return { src: iconUrl, useCurrentColor: false };
	}

	const path = urlWithoutHash.slice(0, queryIndex);
	const query = urlWithoutHash.slice(queryIndex + 1);
	const params = new URLSearchParams(query);
	const useCurrentColor = params.get('currentColor')?.toLowerCase() === 'true';
	params.delete('currentColor');

	const remainingQuery = params.toString();
	const src = `${path}${remainingQuery ? `?${remainingQuery}` : ''}${hash}`;

	return { src, useCurrentColor };
}

export default function IconComponent(props: IconComponentProps) {
	const { API_URL } = React.useContext(ConfigContext);
	const iconUrl = (props as { iconUrl?: string }).iconUrl;
	const iconName = (props as { iconName?: string }).iconName;
	const parsedIconUrl = iconUrl ? parseIconUrl(iconUrl) : undefined;
	const iconSrc = parsedIconUrl?.src.startsWith('/Asset/') ? `${API_URL.replace(/\/$/, '')}${parsedIconUrl.src}` : parsedIconUrl?.src;
	const normalizedIconUrl = iconSrc?.split(/[?#]/)[0].toLowerCase();
	const imageStyle = props.preserveAspectRatio
		? { height: '1.25rem', width: 'auto', maxWidth: props.maxWidth ?? '100%', objectFit: 'contain' as const, display: 'block' }
		: { width: '1.25rem', height: '1.25rem' };
	if (parsedIconUrl?.useCurrentColor && normalizedIconUrl?.endsWith('.svg')) {
		return (
			<span
				role={iconName ? 'img' : undefined}
				aria-label={iconName}
				aria-hidden={iconName ? undefined : true}
				style={{
					display: 'inline-block',
					width: '1.25rem',
					height: '1.25rem',
					flex: '0 0 auto',
					backgroundColor: 'currentColor',
					mask: `url("${iconSrc}") center / contain no-repeat`,
					WebkitMask: `url("${iconSrc}") center / contain no-repeat`,
				}}
			/>
		);
	}
	if (iconSrc) {
		return <img src={iconSrc} alt={iconName ?? ''} style={imageStyle} />;
	}
	if (iconName?.startsWith('devicon-svg:')) {
		const path = iconName.slice('devicon-svg:'.length); // e.g. "react/react-original"
		const src = `${process.env.PUBLIC_URL}/devicons/${path}.svg`;
		return <img src={src} alt={path} style={imageStyle} />;
	}
	if (iconName?.startsWith('devicon-')) {
		return <i className={iconName} style={{ fontSize: '1.25rem', lineHeight: 1 }} />;
	}
	if (iconName && ICON_MAP[iconName]) {
		return ICON_MAP[iconName];
	}
	return <MenuIcon fontSize="small" />;
}
